import {occmClient} from "utils/clients";
import {isNcl} from "utils/app/utils";
import { getLocalizedValue as t} from "../../../i18n";
import { connectorsettings as i18nNamespace } from "../../../i18n/namespace";
import { extractApiFailureMessage } from "utils/api";

const AGENT_TLS_CERTIFICATE_PATH = '/system/config/agent-tls-certificate';

export default ({dispatch, getState}) => next => async action => {
    const {type, payload} = action;
    const useAgentTlsCertificateApi = isNcl();
    const LEARN_MORE_LINK = "https://docs.netapp.com/us-en/console-setup-admin/task-troubleshoot-agent.html"

    if (type === "CONNECTOR-SETTINGS:INSTALL-HTTPS-CERTIFICATE") {
        try {
            dispatch({
                type: "CONNECTOR-SETTINGS:INSTALL-HTTPS-CERTIFICATE-PENDING",
            })

            const {occmCertificate} = payload;
            const {data: certificate} = useAgentTlsCertificateApi
                ? await occmClient.post(`${AGENT_TLS_CERTIFICATE_PATH}/certificate`, {
                    certificate: occmCertificate
                })
                : await occmClient.post('/occm/api/occm/key-store/signed-certificate', {
                    occmCertificate
                });
            dispatch({
                type: "CONNECTOR-SETTINGS:INSTALL-HTTPS-CERTIFICATE-SUCCESS",
                payload: {
                    certificate,
                    ...(useAgentTlsCertificateApi && {certificateType: 'CA_SIGNED'})
                }
            })
        } catch (e) {
            dispatch({
                type: "CONNECTOR-SETTINGS:INSTALL-HTTPS-CERTIFICATE-FAILURE",
            })
            dispatch({
                type: 'NOTIFICATIONS:ERROR',
                payload: {
                    message: t("install_certificate_failed",i18nNamespace),
                    moreInfo: extractApiFailureMessage(e),
                    exception: e,
                    group: 'HTTPS-INSTALL'
                }
            });
        }
    } else if (type === "CONNECTOR-SETTINGS:INSTALL-CA-HTTPS-CERTIFICATE") {
        try {
            dispatch({
                type: "CONNECTOR-SETTINGS:INSTALL-HTTPS-CERTIFICATE-PENDING",
            })
            const {occmCertificate, privateKey} = payload;
            const {data: certificate} = useAgentTlsCertificateApi
                ? await occmClient.post(`${AGENT_TLS_CERTIFICATE_PATH}/certificate`, {
                    certificate: occmCertificate,
                    privateKey
                })
                : await occmClient.post('/occm/api/occm/key-store/external-certificate', {
                    occmCertificate,
                    privateKey
                });
            dispatch({
                type: "CONNECTOR-SETTINGS:INSTALL-HTTPS-CERTIFICATE-SUCCESS",
                payload: {
                    certificate,
                    ...(useAgentTlsCertificateApi && {certificateType: 'EXTERNAL'})
                }
            })

        } catch (e) {            
            dispatch({
                type: "CONNECTOR-SETTINGS:INSTALL-HTTPS-CERTIFICATE-FAILURE",
            })
            dispatch({
                type: 'NOTIFICATIONS:ERROR',
                payload: {
                    message: t("install_ca_certificate_failed",i18nNamespace),
                    moreInfo: extractApiFailureMessage(e),
                    exception: e,
                    group: 'HTTPS-CA-INSTALL'
                }
            });
        }
    } else if (type === "CONNECTOR-SETTINGS:FETCH-HTTPS-CERTIFICATE") {
        try {
            dispatch({
                type: "CONNECTOR-SETTINGS:FETCH-HTTPS-CERTIFICATE-PENDING"
            })
            let certificatePayload;
            if (useAgentTlsCertificateApi) {
                const {data: certificateState} = await occmClient.get(`${AGENT_TLS_CERTIFICATE_PATH}/certificate`);
                const {
                    certificate,
                    certificateType: apiCertificateType,
                    commonName: certificateCommonName,
                    alternativeNames,
                    pendingCsr,
                    csrGeneratedAt
                } = certificateState;
                // NCL reports the provisioning kind on certificateType
                // (SELF_SIGNED, CA_SIGNED, EXTERNAL). The response "type"
                // field is the media type and is not the certificate kind.
                const certificateType = typeof apiCertificateType === 'string'
                    ? apiCertificateType.toUpperCase()
                    : apiCertificateType;
                // signedCertificate stays null for SELF_SIGNED so agent HTTPS
                // setup still opens the install wizard. certificateDetails keeps
                // the payload for the console card status/expiration metrics.
                const signedCertificate = certificateType === 'SELF_SIGNED' ? null : certificate;
                const commonName = (pendingCsr || signedCertificate)
                    ? [certificateCommonName, alternativeNames].filter(Boolean).join(';')
                    : null;
                certificatePayload = {
                    signedCertificate,
                    certificateDetails: certificate,
                    commonName,
                    certificateType,
                    pendingCsr,
                    csrGeneratedAt
                };
            } else {
                const {data: signedCertificate} = await occmClient.get('/occm/api/occm/key-store/signed-certificate');
                const {data: generatedCertificate} = await occmClient.get('/occm/api/occm/key-store/self-certificate');
                const commonNameMatch = /CN=((\w|\*|\.|_|-)+)/.exec(generatedCertificate.subjectName);
                let commonName = commonNameMatch ? commonNameMatch[1] : "";
                commonName += generatedCertificate.alternativeNames ? `;${generatedCertificate.alternativeNames}` : "";
                certificatePayload = {
                    signedCertificate,
                    commonName,
                    certificateLoad: false
                };
            }
            dispatch({
                type: "CONNECTOR-SETTINGS:FETCH-HTTPS-CERTIFICATE-SUCCESS",
                payload: certificatePayload
            })
        } catch (e) {
            dispatch({
                type: "CONNECTOR-SETTINGS:FETCH-HTTPS-CERTIFICATE-FAILURE"
            })
            dispatch({
                type: "NOTIFICATIONS:ERROR-WITH-LEARN-MORE-LINK",
                payload: {
                    message: t("unable_to_load_certificate_info", i18nNamespace),
                    moreInfo: t("default_error_message", i18nNamespace),
                    hyperLink: LEARN_MORE_LINK,
                    linkText: t("documentation_link_text", i18nNamespace),
                    group: 'HTTPS-SETUP'
                }
            });
        }
    } else if (type === "CONNECTOR-SETTINGS:GENERATE-HTTPS-CSR") {
        try {
            const {key, value} = payload;
            dispatch({
                type: "CONNECTOR-SETTINGS:GENERATE-HTTPS-CSR-PENDING"
            });
            const {data: csr} = await occmClient.post(useAgentTlsCertificateApi
                ? `${AGENT_TLS_CERTIFICATE_PATH}/csr`
                : '/occm/api/occm/key-store', {
                [key]: value
            });
            dispatch({
                type: "CONNECTOR-SETTINGS:GENERATE-HTTPS-CSR-SUCCESS",
                payload: {
                    generatedCsr: csr.csr,
                    commonName: value
                }
            });
        } catch (e) {
            dispatch({
                type: "CONNECTOR-SETTINGS:GENERATE-HTTPS-CSR-FAILURE"
            });
            dispatch({
                type: 'NOTIFICATIONS:ERROR',
                payload: {
                    message: t("generate_cert_failed",i18nNamespace),
                    exception: e,
                    group: 'HTTPS-GENERATE'
                }
            });
        }
    }
    return next(action);
}
